import { DocumentListContainer } from '@/features/documents/components/DocumentList/DocumentListContainer'
import { observer } from 'mobx-react-lite'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { EmptyState, EmptyStateDescription } from '@/components/EmptyState'
import { match } from '@/utils/pattern-match-util'
import { DeleteJobDocumentsDialogContainer } from '@/features/jobs/details/components/DeleteJobDocumentsDialog/DeleteJobDocumentsDialogContainer'
import { SupportingItemChecklistContainer } from '@/features/jobs/details/components/DocumentsCard/SupportingItemChecklist/SupportingItemChecklistContainer'
import { DocumentManualVerificationModalContainer } from '@/features/jobs/details/components/DocumentManualVerificationModal/DocumentManualVerificationModalContainer'
import {
  DocumentActionsDropdown,
  DocumentsCard,
} from '@/features/jobs/details/components/DocumentsCard'

export interface DocumentsCardContainerProps {
  vm: JobDetailsViewModel
}

export const DocumentsCardContainer = observer(function DocumentsCardContainer({
  vm,
}: DocumentsCardContainerProps) {
  return (
    <DocumentsCard
      showDownloadAll={vm.canViewDocs && vm.documentsVm.documents.length > 0}
      isDownloadAllPending={vm.documentsVm.downloadAll.pending}
      onDownloadAll={vm.documentsVm.downloadAll}
      actions={
        vm.hasAnyDocPermission &&
        vm.job.status !== 'Closed' && (
          <DocumentActionsDropdownContainer vm={vm} />
        )
      }
      showTabs={vm.documentsVm.hasSupportingItems}
      activeTab={vm.documentsVm.documentsTabController.current.tab}
      onTabChange={vm.documentsVm.documentsTabController.change}
    >
      {match(vm.documentsVm.documentsTabController.current.tab, {
        ClientChecklist: () => {
          return <SupportingItemChecklistContainer vm={vm} />
        },
        Documents: () => {
          return vm.documentsVm.documents.length > 0 ? (
            <DocumentListContainer
              vm={vm.documentsVm.listVm}
              selectable
              compact
              showIcon={false}
            />
          ) : (
            <EmptyState size="card">
              <EmptyStateDescription>
                {vm.job.status === 'Closed'
                  ? 'This job is closed, and you cannot submit any documents at this time.'
                  : 'Add any documents that are related to the job here for easy access and storage.'}
              </EmptyStateDescription>
            </EmptyState>
          )
        },
      })}
      <DocumentsCardDialogsContainer vm={vm} />
    </DocumentsCard>
  )
})

/**
 * Confirmations and the manual-verification modal every documents surface
 * needs, whichever layout lists the files.
 */
export const DocumentsCardDialogsContainer = observer(
  function DocumentsCardDialogsContainer({ vm }: DocumentsCardContainerProps) {
    return (
      <>
        <DeleteJobDocumentsDialogContainer
          vm={vm.documentsVm.deleteSelectedDocumentsDialogVm}
          multiple
        />
        <DocumentManualVerificationModalContainer
          vm={vm.documentsVm.documentManualVerificationVm}
        />
      </>
    )
  },
)

export interface DocumentActionsDropdownContainerProps {
  vm: JobDetailsViewModel
  /**
   * Renders a labelled Upload button instead of the icon trigger.
   */
  labelled?: boolean
}

export const DocumentActionsDropdownContainer = observer(
  function DocumentActionsDropdownContainer({
    vm,
    labelled,
  }: DocumentActionsDropdownContainerProps) {
    const otherType = vm.documentsVm.otherDocumentType
    return (
      <DocumentActionsDropdown
        labelled={labelled}
        hasDocuments={vm.documentsVm.documents.length > 0}
        canUploadDocs={vm.canUploadDocs}
        documentTypes={vm.documentsVm.uploadableDocumentTypes.map((type) => ({
          id: type.id,
          name: type.name,
          onUpload: (files: FileList) =>
            vm.documentsVm.stageFileAsType(files, type),
        }))}
        onUploadOther={
          otherType &&
          ((files: FileList) =>
            vm.documentsVm.stageFileAsType(files, otherType))
        }
        typeQuery={vm.documentsVm.documentTypeQuery}
        onTypeQueryChange={vm.documentsVm.setDocumentTypeQuery}
        onLinkExisting={vm.documentLinkingPickerVm.show}
        canDownloadDocs={vm.canDownloadDocs}
        canRemoveDocs={vm.canRemoveDocs}
        hasDocumentsSelected={vm.hasDocumentsSelected}
        selectedCount={vm.documentsVm.selectedDocuments.size}
        onDownloadSelected={vm.documentsVm.downloadSelected}
        onDeleteSelected={vm.documentsVm.deleteSelected}
      />
    )
  },
)
